import * as React from 'react';
import { SelectBase } from './SelectBase';
import { useSelectGroups } from './hooks/useSelectGroups';
import type {
	IGroupedSelectProps,
	ISelectChangeMeta,
	SelectOptionValue,
	SelectSelectionMode,
} from './types';
import { cn } from '@utils';

export const GroupedSelect = <TValue extends SelectOptionValue = SelectOptionValue, TMetadata = unknown>({
	className,
	disabled = false,
	enableGroupSelectAll = false,
	getGroupSelectAllLabel,
	groups,
	isMultiple = false,
	onChange,
	value,
	...props
}: IGroupedSelectProps<TValue, TMetadata>): React.ReactElement => {
	const selectionMode: SelectSelectionMode = isMultiple ? 'multiple' : 'single';
	const resolvedValue = React.useMemo(() => (isMultiple ? value : value.slice(0, 1)), [isMultiple, value]);
	const { flatOptions, groupedOptions, toggleGroupSelection } = useSelectGroups({
		groups,
		isMultiple,
		onChange,
		value: resolvedValue,
	});

	const handleChange = React.useCallback(
		(nextValue: ReadonlyArray<TValue>, meta: ISelectChangeMeta<TValue, TMetadata>) => {
			if (meta.selectionMode === 'single') {
				onChange(nextValue.slice(0, 1));
				return;
			}

			onChange(nextValue);
		},
		[onChange]
	);

	return (
		<div className={cn('w-full', className)} data-testid="grouped-select">
			<SelectBase
				{...props}
				disabled={disabled}
				enableGroupSelectAll={enableGroupSelectAll && isMultiple}
				getGroupSelectAllLabel={getGroupSelectAllLabel}
				groupedOptions={groupedOptions}
				onChange={handleChange}
				onGroupSelect={toggleGroupSelection}
				options={flatOptions}
				selectionMode={selectionMode}
				value={resolvedValue}
			/>
		</div>
	);
};
